import React from 'react';
import { Box } from '@mui/material';
import { WorkflowInstance, WorkflowInstanceStep } from '../../api/services/workflow.service';
import { status } from '../../theme';
import type { Tone } from '../../components/ui/ToneChip';

// This file used to open with a second design system — a `dna` object of
// hex values (ink #0F172A, border #E2E8F0, ok #10B981, danger #EF4444) that
// sat a few degrees off everything in theme.ts and was applied inline on
// every element of both workflow screens. So a failed workflow was #EF4444
// and an expired document was #DC2626, which reads as a rendering bug
// rather than a decision, and none of it moved when the theme did.
//
// It is gone. Colour comes from the palette, greys come from
// text.primary/text.secondary, borders come from `divider`. What is left
// here is the part that was actually workflow-specific: what a run's state
// is called, what it is waiting on, and how to build the activity feed.

// Status → tone, matching the mobile status language exactly.
export function instanceTone(inst: WorkflowInstance): { tone: Tone; label: string } {
  if (inst.status === 'cancelled') return { tone: 'danger', label: 'cancelled' };
  if (inst.status === 'failed') return { tone: 'danger', label: 'failed' };
  if (inst.status === 'completed') return { tone: 'ok', label: 'completed' };
  const step = inst.steps.find((s) => s.stepId === inst.currentStepId);
  if (step?.status === 'escalated') return { tone: 'danger', label: 'escalated' };
  return { tone: 'warn', label: 'running' };
}

export function isEscalated(inst: WorkflowInstance): boolean {
  return inst.steps.find((s) => s.stepId === inst.currentStepId)?.status === 'escalated';
}

// ------------------------------------------------------------ operations
//
// An operation (a new driver's training) is one row that stays "ongoing"
// for days; its runs are the day-to-day tasks. In the list, what it waits
// on is its open run's current step, or nothing until the next one starts.

/** The run of an operation still open today, if any. */
export const openRunOf = (op: WorkflowInstance, all: WorkflowInstance[]) => all
  .filter((r) => r.parentId === op._id && r.status === 'active')
  .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())[0];

/** Day N of an operation, counting its first day as 1. */
export const operationDay = (op: WorkflowInstance) => {
  const end = op.completedAt ? new Date(op.completedAt) : new Date();
  return Math.max(1, Math.floor((end.getTime() - new Date(op.startedAt).getTime()) / 86400000) + 1);
};

export function operationTone(op: WorkflowInstance, open?: WorkflowInstance): { tone: Tone; label: string } {
  if (op.status !== 'active') return instanceTone(op);
  if (open && isEscalated(open)) return { tone: 'danger', label: 'escalated' };
  return { tone: 'info', label: 'ongoing' };
}

export function operationWaitingOn(op: WorkflowInstance, open?: WorkflowInstance): string {
  if (op.status !== 'active') return waitingOn(op);
  if (!open) return 'Nothing today yet; the next day starts at 7:00';
  const step = open.steps.find((s) => s.stepId === open.currentStepId);
  if (!step) return 'Today’s run is moving on';
  if (step.type === 'timer') return 'Waiting for the end of the day';
  return `Today: ${step.title}`;
}

// What a run is waiting on now — the manager's most useful line.
export function waitingOn(inst: WorkflowInstance): string {
  if (inst.status === 'cancelled') return 'Cancelled';
  if (inst.status === 'failed') return 'Failed';
  if (inst.status === 'completed') return 'Finished';
  const step = inst.steps.find((s) => s.stepId === inst.currentStepId);
  return step ? step.title : '—';
}

// One engine-activity ("pulse") row: tabular time + a plain-voice sentence.
export interface PulseEvent { at: number; text: string; tone: 'late' | 'ok' | 'neutral' }

export function buildPulse(instances: WorkflowInstance[], limit = 20): PulseEvent[] {
  const events: PulseEvent[] = [];
  for (const inst of instances) {
    events.push({ at: new Date(inst.startedAt).getTime(), text: `Started ${inst.label}`, tone: 'neutral' });
    for (const s of inst.steps) {
      if (s.escalatedAt) events.push({ at: new Date(s.escalatedAt).getTime(), text: `Escalated — ${s.title}`, tone: 'late' });
      if (s.completedAt && s.type === 'human_task') {
        events.push({ at: new Date(s.completedAt).getTime(), text: `${s.reason || 'Completed'} — ${s.title}`, tone: 'ok' });
      }
    }
    if (inst.status === 'cancelled' && inst.completedAt) {
      events.push({ at: new Date(inst.completedAt).getTime(), text: `Cancelled — ${inst.label}`, tone: 'late' });
    }
  }
  return events.sort((a, b) => b.at - a.at).slice(0, limit);
}

export const PulseRow: React.FC<{ e: PulseEvent }> = ({ e }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'baseline', py: 0.6 }}>
    <Box
      sx={{
        width: 58, flex: 'none', fontSize: 12,
        color: 'text.disabled', fontVariantNumeric: 'tabular-nums',
      }}
    >
      {new Date(e.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
    </Box>
    <Box sx={{ fontSize: 12.5, color: e.tone === 'late' ? 'error.main' : 'text.secondary', lineHeight: 1.4 }}>
      {e.text}
    </Box>
  </Box>
);

// A step's state as a tone, for the shared pill.
export function stepTone(step: WorkflowInstanceStep): Tone {
  switch (step.status) {
    case 'completed': return 'ok';
    case 'active': return 'warn';
    case 'escalated':
    case 'failed': return 'danger';
    default: return 'neutral';
  }
}

// Icon vocabulary for a step's state (used in the detail timeline).
export function stepDotColor(step: WorkflowInstanceStep): string {
  switch (step.status) {
    case 'completed': return status.ok;
    case 'active': return status.warn;
    case 'escalated':
    case 'failed': return status.danger;
    default: return status.neutral;
  }
}
